import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Sun, CalendarDays, Clock } from 'lucide-react';
import { ViewType } from '@/types/task';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHaptic } from '@/hooks/useHaptic';
import { StatusUpdate } from '@/components/StatusUpdate';

interface ViewTabsProps {
  activeView: ViewType;
  onViewChange: (view: ViewType) => void;
  status?: string | null;
  onUpdateStatus?: (content: string) => Promise<void>;
}

const views = [
  { id: 'today' as ViewType, label: 'Today', icon: Sun },
  { id: 'week' as ViewType, label: 'Week', icon: CalendarDays },
  { id: 'list' as ViewType, label: 'History', icon: Clock },
];

export function ViewTabs({ activeView, onViewChange, status, onUpdateStatus }: ViewTabsProps) {
  const isMobile = useIsMobile();
  const { triggerSelection } = useHaptic();
  const [isConversationOpen, setIsConversationOpen] = useState(false);

  const activeIndex = useMemo(() => {
    return views.findIndex(v => v.id === activeView);
  }, [activeView]);

  // Calculate which index the indicator should be at (including conversation as index 3)
  const indicatorIndex = isConversationOpen ? 3 : activeIndex;

  // Desktop: Simple muted tabs
  if (!isMobile) {
    return (
      <div className="flex gap-1 p-1 rounded-xl bg-muted" data-tour-target="nav-tabs">
        {views.map((view) => {
          const Icon = view.icon;
          const isActive = activeView === view.id;
          
          return (
            <button
              key={view.id}
              onClick={() => {
                triggerSelection();
                onViewChange(view.id);
              }}
              className={cn(
                "relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium",
                "transition-colors duration-200",
                isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-0 bg-card rounded-lg task-shadow"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
              <span className="relative z-10 flex items-center gap-2">
                <Icon className="h-4 w-4" />
                <span>{view.label}</span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  // Mobile: Premium glassmorphic nav with sliding indicator
  const itemSize = 56;
  const gap = 12;
  const slots = onUpdateStatus ? 4 : views.length;
  const railWidth = slots * itemSize + (slots - 1) * gap;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-6 pt-2 pb-safe" data-tour-target="nav-tabs">
      <div className="mobile-nav-glass rounded-3xl p-3">
        {/* Fixed-width rail so indicator math always matches button positions */}
        <div className="relative mx-auto" style={{ width: railWidth }}>
          {/* Sliding indicator */}
          <motion.div
            className="absolute left-0 top-0 nav-indicator-glow rounded-2xl"
            style={{ width: itemSize, height: itemSize }}
            initial={false}
            animate={{ x: indicatorIndex * (itemSize + gap) }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          />

          <div
            className={cn(
              'relative z-10 grid gap-3',
              onUpdateStatus ? 'grid-cols-4' : 'grid-cols-3'
            )}
          >
            {/* Navigation items */}
            {views.map((view) => {
              const Icon = view.icon;
              const isActive = activeView === view.id && !isConversationOpen;

              return (
                <motion.button
                  key={view.id}
                  onClick={() => {
                    triggerSelection();
                    setIsConversationOpen(false);
                    onViewChange(view.id);
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center rounded-2xl"
                  style={{ width: itemSize, height: itemSize }}
                >
                  <Icon
                    className={cn(
                      'h-7 w-7 transition-all duration-300',
                      isActive
                        ? 'stroke-white scale-110'
                        : 'stroke-white/60 hover:stroke-white/90'
                    )}
                    strokeWidth={2}
                  />
                </motion.button>
              );
            })}

            {/* Conversation button - integrated into indicator system */}
            {onUpdateStatus && (
              <StatusUpdate
                status={status || null}
                onUpdate={onUpdateStatus}
                variant="inline"
                isHighlighted={isConversationOpen}
                dataTourTarget="history-note-button"
                onOpenChange={(open) => {
                  if (open) {
                    triggerSelection();
                  }
                  setIsConversationOpen(open);
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
